import { useDataQuery, type DataQueryOptions } from './useDataQuery';
import type {
  ListEnvelope,
  WalletSummary,
  WalletPnlSeriesEnvelope,
  WalletDepositEvent,
  WalletRedeemRequest,
  WalletCashflowPoint,
  WalletActivityEvent,
  WalletVaultCap,
} from './types';

/**
 * Per-wallet analytics hooks — the console's "my analytics" surface. Every hook
 * hits an AUTHENTICATED `/wallets/:wallet/*` endpoint (`authenticated: true`), so
 * the DataApiProvider's bearer token (the signed-in user's Google ID token) is
 * sent and the server enforces own-wallet-only access. Requests are gated on a
 * wallet being present; the token gate is handled inside useDataQuery.
 *
 * `mastVaults` optionally restricts aggregation to a specific mast; omitted, the
 * server auto-discovers every vault the wallet has deposited in. `assetDecimals`
 * normalizes the native cBTC columns (a mast shares one asset).
 */

const seg = (v: string | undefined) => encodeURIComponent(v ?? '');

interface WalletBaseParams {
  mastVaults?: readonly string[];
  assetDecimals: number;
}
interface WalletRangeParams extends WalletBaseParams {
  from?: number;
  to?: number;
}
interface WalletPageParams extends WalletBaseParams {
  limit?: number;
  offset?: number;
}

const vaultsParam = (mast?: readonly string[]) =>
  mast && mast.length > 0 ? mast.join(',') : undefined;

/** Position summary + per-vault breakdown (modules 1 & 7). */
export function useWalletSummary(
  wallet: string | undefined,
  params: WalletBaseParams,
  options?: DataQueryOptions<WalletSummary>,
) {
  return useDataQuery<WalletSummary>({
    path: `/wallets/${seg(wallet)}/summary`,
    params: { vaults: vaultsParam(params.mastVaults), assetDecimals: params.assetDecimals },
    tier: 'live',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}

/** Cumulative PnL + portfolio value over time (modules 2 & 3). */
export function useWalletPnlSeries(
  wallet: string | undefined,
  params: WalletBaseParams,
  options?: DataQueryOptions<WalletPnlSeriesEnvelope>,
) {
  return useDataQuery<WalletPnlSeriesEnvelope>({
    path: `/wallets/${seg(wallet)}/pnl-series`,
    params: { vaults: vaultsParam(params.mastVaults), assetDecimals: params.assetDecimals },
    tier: 'live',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}

/** Deposit history over a window, paginated (module 4). */
export function useWalletDeposits(
  wallet: string | undefined,
  params: WalletRangeParams & { limit?: number; offset?: number },
  options?: DataQueryOptions<ListEnvelope<WalletDepositEvent>>,
) {
  return useDataQuery<ListEnvelope<WalletDepositEvent>>({
    path: `/wallets/${seg(wallet)}/deposits`,
    params: {
      vaults: vaultsParam(params.mastVaults),
      assetDecimals: params.assetDecimals,
      from: params.from,
      to: params.to,
      limit: params.limit,
      offset: params.offset,
    },
    tier: 'reference',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}

/** Redeem lifecycle over a window, paginated, optional status (module 5). */
export function useWalletRedeems(
  wallet: string | undefined,
  params: WalletRangeParams & { limit?: number; offset?: number; status?: string },
  options?: DataQueryOptions<ListEnvelope<WalletRedeemRequest>>,
) {
  return useDataQuery<ListEnvelope<WalletRedeemRequest>>({
    path: `/wallets/${seg(wallet)}/redeems`,
    params: {
      vaults: vaultsParam(params.mastVaults),
      assetDecimals: params.assetDecimals,
      status: params.status,
      from: params.from,
      to: params.to,
      limit: params.limit,
      offset: params.offset,
    },
    tier: 'reference',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}

/** Daily deposit/redeem cashflow timemap (module 6). */
export function useWalletCashflows(
  wallet: string | undefined,
  params: WalletRangeParams,
  options?: DataQueryOptions<ListEnvelope<WalletCashflowPoint>>,
) {
  return useDataQuery<ListEnvelope<WalletCashflowPoint>>({
    path: `/wallets/${seg(wallet)}/cashflows`,
    params: {
      vaults: vaultsParam(params.mastVaults),
      assetDecimals: params.assetDecimals,
      from: params.from,
      to: params.to,
    },
    tier: 'live',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}

/** Unified chronological activity feed, paginated (module 8). */
export function useWalletActivity(
  wallet: string | undefined,
  params: WalletPageParams,
  options?: DataQueryOptions<ListEnvelope<WalletActivityEvent>>,
) {
  return useDataQuery<ListEnvelope<WalletActivityEvent>>({
    path: `/wallets/${seg(wallet)}/activity`,
    params: {
      vaults: vaultsParam(params.mastVaults),
      assetDecimals: params.assetDecimals,
      limit: params.limit,
      offset: params.offset,
    },
    tier: 'reference',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}

/** Per-vault deposit cap, headroom and consent (module 9). */
export function useWalletCaps(
  wallet: string | undefined,
  params: WalletBaseParams,
  options?: DataQueryOptions<ListEnvelope<WalletVaultCap>>,
) {
  return useDataQuery<ListEnvelope<WalletVaultCap>>({
    path: `/wallets/${seg(wallet)}/caps`,
    params: { vaults: vaultsParam(params.mastVaults), assetDecimals: params.assetDecimals },
    tier: 'live',
    enabled: !!wallet,
    authenticated: true,
    options,
  });
}
